import { Link, useLocation, useNavigate } from "react-router";
import { ListaaLogo } from "./ListaaLogo";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Menu, X, Home, Compass, CalendarDays, Users, Heart, Star, Settings, LogOut, User, Crown, UserPlus, Loader2, Sun, Moon, Sparkles, LayoutDashboard, Edit3, Map, MessageSquare, Camera, MessageSquareText, Ban, CreditCard, Plus } from "lucide-react";
import { useThemeContext } from "./ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { useChatStore } from "@/store/chatStore";
import { useEventsPageEnabled } from "@/hooks/useEventsPageEnabled";

import { ProfileAvatar } from "./ProfileAvatar";
import { NotificationBell } from "./NotificationBell";
import { ADD_VENUE_URL, useAddVenueGate } from "./AddVenueMemberDialog";

/** `/` matches the homepage only; every other link matches its path prefix (ignoring `#`/`?`). */
function isNavLinkActive(to: string, pathname: string): boolean {
  const path = to.split(/[?#]/)[0];
  return path === "/" ? pathname === "/" : pathname.startsWith(path);
}

export default function Navigation() {
  const eventsEnabled = useEventsPageEnabled();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, setTheme, isLight, isGlassy } = useThemeContext();
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const pendingChatRequests = useChatStore((s) => s.pendingRequestsCount());
  const isMember = user?.accountType === "member";
  // Venue owners manage their listing — the marketplace nav links are noise for them.
  const showNavLinks = user?.accountType !== "venue";
  const addVenueGate = useAddVenueGate();

  // Get user display name
  const getUserDisplayName = () => {
    if (!user?.name) return "User";
    const firstName = user.name.split(" ")[0];
    return firstName;
  };

  // Get account type label
  const getAccountTypeLabel = () => {
    if (!user) return "";
    return user.accountType === "venue" ? "Venue Owner" : "Member";
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    setMobileMenuOpen(false);
    navigate("/");
  };

  // Track scroll position for background opacity
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [mobileMenuOpen]);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close profile dropdown on route change
  useEffect(() => {
    setProfileOpen(false);
  }, [location.pathname]);

  // Profile menu items based on account type
  const memberProfileMenuItems = [
    { to: "/member/?section=profile", label: "My Profile", icon: User },
    { to: "/account-settings", label: "Account Settings", icon: Settings },
    { to: "/member/?section=preferences", label: "Preferences", icon: Star },
    { to: "/member/?section=favorites", label: "Favorites", icon: Heart },
    { to: "/member/operations/reservations", label: "Bookings", icon: CreditCard },
    { to: "/member/photos", label: "My Photos", icon: Camera },
    { to: "/member/?section=reviews", label: "My Reviews", icon: MessageSquareText },
    { to: "/member/?section=blocked", label: "Blocked Members", icon: Ban },
    { to: "/member/chat", label: "Chat", icon: MessageSquare },
  ];

  const venueProfileMenuItems = [
    { to: "/venue-dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/steps/venue-information", label: "Edit Venue", icon: Edit3 },
    { to: "/account-settings", label: "Account Settings", icon: Settings },
  ];

  // Get profile menu items based on user type
  const profileMenuItems = user?.accountType === "venue"
    ? venueProfileMenuItems
    : memberProfileMenuItems;

  // Additional menu items based on account type (shown in secondary section)
  const memberSecondaryItems = [
    { to: "/membership-benefits", label: "Membership Benefits", icon: Crown },
    { to: "/invite-friends", label: "Invite Friends", icon: UserPlus },
  ];

  const venueSecondaryItems: { to: string; label: string; icon: typeof Crown }[] = [];

  // Top-level header nav. Pricing and For Venues live in the footer links instead.
  const navLinks: {
    to: string;
    label: string;
    icon: typeof Home;
    /** Needed where two items share a path (`/search/map` vs. its people mode). */
    match?: (pathname: string, search: string) => boolean;
  }[] = [
    { to: "/", label: "Home", icon: Home },
    { to: "/venues", label: "Venues", icon: Compass },
    ...(eventsEnabled === false ? [] : [{ to: "/events", label: "Events", icon: CalendarDays }]),
    {
      to: "/search/map?mode=people",
      label: "Members",
      icon: Users,
      match: (pathname, search) =>
        pathname.startsWith("/search/map") && new URLSearchParams(search).get("mode") === "people",
    },
    { to: "/services", label: "Talents", icon: Sparkles },
    {
      to: "/search/map",
      label: "Map Search",
      icon: Map,
      match: (pathname, search) =>
        pathname.startsWith("/search/map") && new URLSearchParams(search).get("mode") !== "people",
    },
  ];

  return (
    <div
      className={`sticky top-0 z-30 backdrop-blur-xl transition-all duration-300 ${
        scrolled
          ? isGlassy
            ? "bg-[#0b0b1a]/70 backdrop-blur-2xl border-b border-white/[0.08] shadow-lg shadow-purple-900/10"
            : isLight
            ? "bg-white/90 border-b border-gray-200/60 shadow-sm"
            : "bg-black/90 border-b border-white/10 shadow-lg shadow-black/20"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3 flex items-center justify-between">
        {/* Logo */}
        <div className="flex flex-col">
          <Link to={user?.accountType === "venue" ? "/steps/venue-information" : "/"} aria-label="Listaa home" className="hover:opacity-80 transition-opacity self-center">
            <ListaaLogo isLight={isLight} className="h-8" />
          </Link>
          <div className={`text-[10px] ${isLight ? "text-gray-500" : "text-white/60"} hidden sm:block`}>
            Marketplace & Community
          </div>
        </div>

        {/* Center Floating Nav Links - Desktop */}
        {showNavLinks && location.pathname !== "/get-started" && (
        <nav className={`hidden lg:flex items-center gap-1 backdrop-blur-xl ${isGlassy ? "bg-white/[0.06] border-white/[0.1]" : isLight ? "bg-gray-900/[0.04] border-gray-200/60" : "bg-white/5 border-white/10"} rounded-full border px-6 py-2`}>
          {navLinks.map((link) => {
            const isActive = link.match
              ? link.match(location.pathname, location.search)
              : isNavLinkActive(link.to, location.pathname);
            return (
              <Link
                key={link.to}
                to={link.to}
                className={`transition-colors text-sm px-4 py-2 rounded-full ${
                  isActive
                    ? isGlassy
                      ? "text-white bg-white/[0.12] font-medium backdrop-blur-md"
                      : isLight
                      ? "text-gray-900 bg-gray-900/[0.08] font-medium"
                      : "text-white bg-white/10 font-medium"
                    : isGlassy
                      ? "text-white/70 hover:text-white hover:bg-white/[0.08]"
                      : isLight
                      ? "text-gray-600 hover:text-gray-900 hover:bg-gray-900/[0.05]"
                      : "text-white/80 hover:text-white hover:bg-white/5"
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>
        )}

        {/* Right Actions - Desktop */}
        <div className="hidden lg:flex items-center gap-3">
          {!addVenueGate.hidden && (
            <a
              href={ADD_VENUE_URL}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-[#e0245a] text-white hover:bg-[#e0245a]/90 transition-colors"
            >
              <Plus size={15} />
              Add Venue
            </a>
          )}
          {/* Theme Switcher */}
          <div className={`flex items-center gap-0.5 p-1 rounded-full border ${isGlassy ? "bg-white/[0.06] border-white/[0.1]" : isLight ? "bg-gray-100 border-gray-200/60" : "bg-white/5 border-white/10"}`}>
            {([
              { value: "light" as const, icon: Sun, label: "Light" },
              { value: "dark" as const, icon: Moon, label: "Dark" },
              { value: "glassy" as const, icon: Sparkles, label: "Glassy" },
            ]).map(({ value, icon: Icon, label }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                title={label}
                className={`relative p-1.5 rounded-full transition-all duration-200 cursor-pointer ${
                  theme === value
                    ? isGlassy
                      ? "bg-white/[0.15] text-white shadow-sm shadow-purple-500/20"
                      : isLight
                      ? "bg-white text-gray-900 shadow-sm"
                      : "bg-white/15 text-white shadow-sm"
                    : isGlassy
                    ? "text-white/40 hover:text-white/70"
                    : isLight
                    ? "text-gray-400 hover:text-gray-600"
                    : "text-white/40 hover:text-white/70"
                }`}
              >
                <Icon size={14} />
              </button>
            ))}
          </div>

          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          ) : isAuthenticated && user ? (
            <>
              {/* Notifications - member only */}
              {isMember && <NotificationBell isLight={isLight} isGlassy={isGlassy} />}

              {/* Profile Avatar & Dropdown */}
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="w-9 h-9 rounded-full overflow-hidden border-2 border-transparent hover:border-[#ff3366]/50 transition-all cursor-pointer ring-0 hover:ring-2 hover:ring-[#ff3366]/20"
                >
                  <ProfileAvatar
                    src={user.avatar}
                    name={user.name}
                    alt={user.name || "Profile"}
                    className="w-full h-full rounded-full"
                    isLight={isLight}
                  />
                </button>

                {/* Dropdown Menu */}
                {profileOpen && (
                  <div className={`absolute right-0 top-full z-50 mt-2 w-56 ${isGlassy ? "bg-[#12121c]/80 border-white/[0.12] shadow-2xl shadow-purple-900/20 backdrop-blur-2xl" : isLight ? "bg-white/95 border-gray-200/80 shadow-xl shadow-black/10" : "bg-[#12121c]/95 border-white/[0.1] shadow-2xl shadow-black/50"} backdrop-blur-xl border rounded-xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200`}>
                    {/* Profile header */}
                    <div className={`px-4 py-3 border-b ${isLight ? "border-gray-200/60" : "border-white/[0.06]"}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full overflow-hidden border ${isLight ? "border-purple-300/50" : "border-purple-500/30"} shrink-0`}>
                          <ProfileAvatar
                            src={user.avatar}
                            name={user.name}
                            alt={user.name || "Profile"}
                            className="w-full h-full rounded-full"
                            isLight={isLight}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className={`${isLight ? "text-gray-900" : "text-white"} text-sm font-semibold truncate`}>{getUserDisplayName()}</p>
                          <p className={`${isLight ? "text-gray-500" : "text-gray-500"} text-[11px]`}>{getAccountTypeLabel()}</p>
                        </div>
                      </div>
                    </div>

                    {/* Menu items */}
                    <div className="py-1.5">
                      {profileMenuItems.map((item) => {
                        const Icon = item.icon;
                        const showRequestBadge =
                          isMember && item.to === "/member/chat" && pendingChatRequests > 0;
                        return (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={() => setProfileOpen(false)}
                            className={`flex items-center gap-3 px-4 py-2.5 text-sm ${isLight ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100/80" : "text-gray-400 hover:text-white hover:bg-white/[0.06]"} transition-colors`}
                          >
                            <Icon size={15} className={isLight ? "text-gray-400" : "text-gray-500"} />
                            <span className="flex-1">{item.label}</span>
                            {showRequestBadge && (
                              <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff3366] text-white text-[10px] flex items-center justify-center">
                                {pendingChatRequests}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                      <div className={`my-1 mx-3 border-t ${isLight ? "border-gray-200/60" : "border-white/[0.06]"}`} />
                      {(user?.accountType === "venue" ? venueSecondaryItems : memberSecondaryItems).map((item) => {
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={() => setProfileOpen(false)}
                            className={`flex items-center gap-3 px-4 py-2.5 text-sm ${isLight ? "text-gray-600 hover:text-gray-900 hover:bg-gray-100/80" : "text-gray-400 hover:text-white hover:bg-white/[0.06]"} transition-colors`}
                          >
                            <Icon size={15} className={isLight ? "text-gray-400" : "text-gray-500"} />
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>

                    {/* Logout */}
                    <div className={`border-t ${isLight ? "border-gray-200/60" : "border-white/[0.06]"} py-1.5`}>
                      <button
                        onClick={handleLogout}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm ${isLight ? "text-gray-600 hover:text-[#ff3366] hover:bg-gray-100/80" : "text-gray-400 hover:text-[#ff3366] hover:bg-white/[0.06]"} transition-colors w-full cursor-pointer`}
                      >
                        <LogOut size={15} className={isLight ? "text-gray-400" : "text-gray-500"} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
              <Link
                to="/login"
                state={{ signIn: true }}
                className={`group flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                  isGlassy
                    ? "border-white/[0.15] text-white/90 hover:text-white bg-white/[0.06] hover:bg-white/[0.12] hover:border-white/[0.25] hover:shadow-lg hover:shadow-purple-500/10"
                    : isLight
                    ? "border-gray-300 text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-50 hover:border-gray-400 hover:shadow-md hover:shadow-black/5"
                    : "border-white/[0.12] text-white/90 hover:text-white bg-white/[0.05] hover:bg-white/[0.1] hover:border-white/[0.2] hover:shadow-lg hover:shadow-black/20"
                }`}
              >
                <User size={15} className="opacity-60 group-hover:opacity-100 transition-opacity" />
                Sign In
              </Link>
          )}
        </div>

        {/* Mobile Hamburger Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`lg:hidden w-10 h-10 rounded-xl ${isGlassy ? "bg-white/[0.06] border-white/[0.1] text-white hover:bg-white/[0.1]" : isLight ? "bg-gray-900/[0.05] border-gray-200/60 text-gray-700 hover:bg-gray-900/[0.1]" : "bg-white/5 border-white/10 text-white hover:bg-white/10"} border flex items-center justify-center transition-colors`}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile Menu Overlay - rendered via portal to escape sticky/backdrop-filter stacking context */}
      {mobileMenuOpen && createPortal(
        <div
          className="lg:hidden fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className={`absolute top-0 left-0 w-[85%] max-w-[340px] h-[100dvh] flex flex-col ${isLight ? "bg-white border-r border-gray-200/60" : "bg-[#0a0a0a] border-r border-[#1a1a1a]"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div className={`shrink-0 flex items-center justify-between px-5 py-4 border-b safe-area-top ${isLight ? "border-gray-200/60" : "border-[#1a1a1a]"}`}>
              {isAuthenticated && user ? (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-purple-500/30 shrink-0">
                    <ProfileAvatar
                      src={user.avatar}
                      name={user.name}
                      alt={user.name || "Profile"}
                      className="w-full h-full rounded-full"
                      isLight={isLight}
                    />
                  </div>
                  <div>
                    <span className={`font-bold text-sm ${isLight ? "text-gray-900" : "text-white"}`}>{getUserDisplayName()}</span>
                    <p className={`text-[10px] mt-0.5 ${isLight ? "text-gray-500" : "text-white/40"}`}>{getAccountTypeLabel()}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col">
                  <ListaaLogo isLight={isLight} className="h-7 self-start" />
                  <span className={`text-[10px] ${isLight ? "text-gray-500" : "text-white/40"}`}>Marketplace & Community</span>
                </div>
              )}
              <button
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${isLight ? "bg-gray-100 border border-gray-200/60 text-gray-600 hover:bg-gray-200" : "bg-white/5 border border-white/10 text-white hover:bg-white/10"}`}
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
            {/* Profile Links - only show when authenticated */}
            {isAuthenticated && user && (
              <>
                <p className={`px-6 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider ${isLight ? "text-gray-400" : "text-white/30"}`}>Account</p>
                <div className="px-3 pb-3 space-y-0.5">
                  {profileMenuItems.map((item) => {
                    const Icon = item.icon;
                    const showRequestBadge =
                      isMember && item.to === "/member/chat" && pendingChatRequests > 0;
                    return (
                      <Link
                        key={item.label}
                        to={item.to}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isLight ? "text-gray-700 hover:text-gray-900 hover:bg-gray-100" : "text-white/75 hover:text-white hover:bg-white/5"}`}
                      >
                        <Icon size={16} className="text-purple-400 shrink-0" />
                        <span className="flex-1">{item.label}</span>
                        {showRequestBadge && (
                          <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff3366] text-white text-[10px] flex items-center justify-center">
                            {pendingChatRequests}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className={`mx-5 border-t ${isLight ? "border-gray-200/60" : "border-[#1a1a1a]"}`}></div>
              </>
            )}

            {/* Nav Links */}
            {showNavLinks && (<>
            <p className={`px-6 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider ${isLight ? "text-gray-400" : "text-white/30"}`}>Explore</p>
            <div className="px-3 pb-3 space-y-0.5">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isLight ? "text-gray-700 hover:text-gray-900 hover:bg-gray-100" : "text-white/80 hover:text-white hover:bg-white/5"}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-[#ff3366]/10 border border-[#ff3366]/20 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} className="text-[#ff3366]" />
                    </div>
                    <span className="text-sm font-medium">{link.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Divider */}
            <div className={`mx-5 border-t ${isLight ? "border-gray-200/60" : "border-[#1a1a1a]"}`}></div>
            </>)}

            {/* Theme Switcher - Mobile */}
            <div className="px-5 py-4">
              <p className={`text-xs font-medium mb-3 ${isLight ? "text-gray-500" : "text-white/40"}`}>Theme</p>
              <div className={`flex items-center gap-1 p-1 rounded-xl border ${isGlassy ? "bg-white/[0.06] border-white/[0.1]" : isLight ? "bg-gray-100 border-gray-200/60" : "bg-white/5 border-white/10"}`}>
                {([
                  { value: "light" as const, icon: Sun, label: "Light" },
                  { value: "dark" as const, icon: Moon, label: "Dark" },
                  { value: "glassy" as const, icon: Sparkles, label: "Glassy" },
                ]).map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    onClick={() => setTheme(value)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-all duration-200 cursor-pointer ${
                      theme === value
                        ? isGlassy
                          ? "bg-white/[0.15] text-white shadow-sm shadow-purple-500/20"
                          : isLight
                          ? "bg-white text-gray-900 shadow-sm"
                          : "bg-white/15 text-white shadow-sm"
                        : isGlassy
                        ? "text-white/40 hover:text-white/70"
                        : isLight
                        ? "text-gray-400 hover:text-gray-600"
                        : "text-white/40 hover:text-white/70"
                    }`}
                  >
                    <Icon size={14} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className={`mx-5 border-t ${isLight ? "border-gray-200/60" : "border-[#1a1a1a]"}`}></div>

            {/* Auth Actions */}
            <div className="px-3 py-4 space-y-2">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className={`flex items-center justify-center gap-2 mx-2 py-3 rounded-xl w-[calc(100%-1rem)] ${isLight ? "bg-gray-100 text-gray-700 hover:bg-gray-200" : "bg-white/5 text-white hover:bg-white/10"} text-sm font-medium transition-colors`}
                >
                  <LogOut size={16} />
                  Sign Out
                </button>
              ) : (
                  <Link
                    to="/login"
                    state={{ signIn: true }}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center justify-center gap-2 mx-2 py-3.5 rounded-xl border text-sm font-semibold transition-all duration-200 ${
                      isGlassy
                        ? "border-white/[0.12] text-white bg-white/[0.06] hover:bg-white/[0.12] hover:border-white/[0.2]"
                        : isLight
                        ? "border-gray-300 text-gray-800 bg-white hover:bg-gray-50 hover:border-gray-400"
                        : "border-white/[0.1] text-white bg-white/[0.05] hover:bg-white/[0.1] hover:border-white/[0.18]"
                    }`}
                  >
                    <User size={16} className="opacity-70" />
                    Sign In
                  </Link>
              )}
            </div>
            </div>

            {/* Bottom Branding */}
            <div className={`shrink-0 px-5 py-3 border-t pb-[max(0.75rem,env(safe-area-inset-bottom))] ${isLight ? "border-gray-200/60 bg-white" : "border-[#1a1a1a] bg-[#0a0a0a]"}`}>
              <p className="text-gray-600 text-[11px] text-center">
                &copy; 2026 Listaa. All rights reserved.
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
